import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';

import { Card } from 'react-bootstrap';
import DOMPurify from 'dompurify';

import { Button, Tooltip } from '@sumologic/yama-ds-components';

import User from '~/models/User';
import Runbook from '~/models/Runbook';
import { checkFeatureFlag } from '~/modules/helpers';
import { formatAsUserShortDateTime } from '~/modules/helpers/date';

import Icon from '~/components/Icon/Icon';
import Loader from '~/components/Loader/Loader';
import Translate from '~/components/Translate/Translate';

import Collapse, { CollapseContext } from '~/containers/Collapse/Collapse';
import {
    InfoTooltip,
    getStatusIcon
} from '~/containers/ActionResult/sharedFunctions/sharedFunctionsAndComponents';

/**
 * Component represent the result from a runbook node of user-choice type
 * @return {React.node}
 * @constructor
 * @component
 * @author FM
 */
function ResultUserChoiceCard({ data, collapsible, isAuthenticated }) {
    const [userName, setUserName] = useState();
    const collapseContext = useContext(CollapseContext);
    const isCollapsed = collapseContext.getCollapse(data.id || data.unique_id);

    const getUserDetails = async () => {
        const id = data?.user_id;
        if (data.authorizer_id === -1) {
            const actConfAuth = data.act_conf.authorizer;
            const formattedAuthArray = [];

            for (const e of actConfAuth) {
                if (Number.isInteger(e)) {
                    try {
                        const response = await User.getDetails(e);
                        const userDetails = response.data.data;
                        formattedAuthArray.push(
                            `${userDetails.name} ${userDetails.surname}`
                        );
                    } catch (exception) {
                        console.error(exception);
                    }
                } else {
                    formattedAuthArray.push(e);
                }
            }

            setUserName(formattedAuthArray.join(', '));
        } else if (id && isAuthenticated && checkFeatureFlag('full_csoar')) {
            User.getDetails(id)
                .then(response => {
                    const e = response.data?.data;
                    if (200 !== response.status || !e) {
                        throw response;
                    } else {
                        setUserName(`${e.name} ${e.surname}`);
                    }
                })
                .catch(exception => {
                    console.error(exception);
                });
        }
    };

    let status = data.run; // <<
    switch (status) {
        case 2:
            status = 'running';
            break;
        case 1:
            status = 'success';
            break;
        case -1:
            status = 'failed';
            break;
    }

    const HeaderTitle = ({ children, targetId }) => {
        if (collapsible) {
            return (
                <Collapse.Toggle targetId={targetId}>
                    <Button variant="text" color="primary" className="title">
                        {children}
                    </Button>
                </Collapse.Toggle>
            );
        }
        return <span className="title">{children}</span>;
    };

    HeaderTitle.propTypes = {
        children: PropTypes.node,
        targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    };

    const infoTooltip = () => {
        let info = [
            {
                key: 'Execution Date',
                value: formatAsUserShortDateTime(data.created_on)
            },
            ...(data?.act_conf?.title
                ? [
                      {
                          key: 'Title',
                          value: data.act_conf.title
                      }
                  ]
                : []),
            {
                key: 'User',
                value: userName ?? '-'
            }
        ];

        return <InfoTooltip info={info} />;
    };

    const Actions = () => {
        return (
            <span className="actions">
                <Tooltip
                    title={<div className="w-100">{infoTooltip()}</div>}
                    placement="bottom"
                    arrow
                    onOpen={() => {
                        getUserDetails();
                    }}
                    classes={{
                        tooltip: 'action-result-tooltip'
                    }}
                >
                    <div className="d-flex flex-grow-1 align-items-center">
                        <Icon type="information" />
                    </div>
                </Tooltip>
            </span>
        );
    };

    const bodyClasses = ['p-0', 'm-0'];

    const checkAuthorizer = auth => {
        const parsedAuth = auth ? parseInt(auth) : auth;
        const havePermissions = data.triage_id
            ? true
            : User.can([
                  'playbook_incident_manage',
                  'observability_management'
              ]);
        return (
            havePermissions &&
            (!parsedAuth ||
                parsedAuth === -1 ||
                parsedAuth === '0' ||
                parseInt(CURRENT_USER.id) === parsedAuth ||
                CURRENT_USER.isInGroup(parsedAuth))
        );
    };

    const answerUserChoice = (runbookId, runningId, userChoiceId, answer) => {
        Runbook.answerUserChoice(
            runbookId,
            runningId,
            userChoiceId,
            answer
        ).catch(exception => {
            console.error(exception);
        });
    };

    const isLoading = false; /*status === 'running'*/

    let result = '';

    if (!isLoading) {
        const userChoiseResult = () => {
            const sendAnswer = answer => {
                answerUserChoice(
                    data.playbook_id,
                    data.running_id,
                    data.playbook_action,
                    answer
                );
            };
            let answer = '';
            if (2 !== data.run && data.status) {
                answer = data.status;
            }
            return (
                <div className="card-input-table-container p-3">
                    {data?.act_conf?.title && (
                        <div className="uc-col">
                            <div className="uc-col-label">Title</div>
                            <div>{data.act_conf.title}</div>
                        </div>
                    )}
                    <div className="uc-col">
                        <div className="uc-col-label">Question</div>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    data.act_conf.message_parsed ||
                                        data.act_conf.question
                                )
                            }}
                        />
                    </div>
                    {data.run === 2 &&
                    checkAuthorizer(data.authorizer_id) &&
                    data.act_conf.answers ? (
                        <div className="buttons-container ps-1">
                            {data.act_conf.answers.map(answer => {
                                return (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            sendAnswer(answer);
                                        }}
                                    >
                                        {answer}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : data.run !== 2 ? (
                        <div className="uc-col">
                            <div className="uc-col-label">Answer</div>
                            <div>{answer}</div>
                        </div>
                    ) : (
                        <span className="waiting-btn border border-warning text-warning rounded ">
                            <Translate message="Waiting auth" />
                        </span>
                    )}
                </div>
            );
        };

        result = userChoiseResult();
    }

    let cardClasses = ['action-result', 'mb-4', 'user-choice-result'];

    const CollapseIcon = ({ targetId }) => {
        const iconType = isCollapsed ? 'chevron-down' : 'chevron-up';
        return (
            <Collapse.Toggle targetId={targetId}>
                <div className="collapse-icon">
                    <Icon type={iconType} />
                </div>
            </Collapse.Toggle>
        );
    };

    CollapseIcon.propTypes = {
        targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    };

    const WrapWithCollapseContent = ({ children }) => {
        return collapsible ? (
            <Collapse.Content id={data.id || data.unique_id}>
                {children}
            </Collapse.Content>
        ) : (
            children
        );
    };

    const errors = () => {
        const errors = data.error;
        if ('string' === typeof errors) {
            return errors;
        }
        return '-';
    };

    return (
        <Card className={cardClasses.join(' ')}>
            <Card.Header>
                <div className="d-flex align-items-center w-100">
                    <CollapseIcon targetId={data.id || data.unique_id} />
                    <div className="flex-grow-1 left-elements">
                        <div className="header-title-container">
                            <HeaderTitle targetId={data.id || data.unique_id}>
                                <Translate message="User choice" />
                            </HeaderTitle>
                        </div>
                        <Actions />
                    </div>
                    <div className="flex-grow-1 center-elements" />
                    <div className="flex-grow-1 right-elements">
                        <span className="status">{getStatusIcon(status)}</span>
                    </div>
                </div>
            </Card.Header>
            {status !== 'failed' && (
                <WrapWithCollapseContent>
                    <Card.Body className={bodyClasses.join(' ')}>
                        {isLoading ? (
                            <Loader />
                        ) : (
                            <div className="result-content">{result}</div>
                        )}
                    </Card.Body>
                </WrapWithCollapseContent>
            )}
            {data?.error && (
                <WrapWithCollapseContent>
                    <Card.Body className={bodyClasses.join(' ')}>
                        <div className="error-content">
                            <div className="text-danger mb-0 alert alert-sm border border-danger rounded">
                                <Translate
                                    message="ERROR: {errormessage}"
                                    values={{
                                        errormessage: errors()
                                    }}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </WrapWithCollapseContent>
            )}
        </Card>
    );
}

ResultUserChoiceCard.propTypes = {
    /** Is card collapsible */
    collapsible: PropTypes.bool,
    /** Object represent the result */
    data: PropTypes.object
};

ResultUserChoiceCard.defaultProps = {
    isAuthenticated: true,
    collapsible: true,
    data: {}
};

export default ResultUserChoiceCard;
